import { useWSQuery, WSQueryConfig } from "@ws-react-query";
import { AuditLog, AuditLogResource } from "@wingspanhq/wingspan-api-sdk";
import { auditLogsApi } from "@api";
import { queriesAuditLogs } from "@query";
import { selectorMergedAuditLogs } from "../../selectors/selectorMergedAuditLogs";
import { WSServiceError } from "../../../utils/serviceHelper";

type Request = {
  resources: AuditLogResource[];
  pageSize: number;
  xWingspanAccount?: string;
};

/**
 * One merged audit-log timeline across a set of resources.
 *
 * `listAuditLogs` filters on exactly one `resourceType` + `resourceId` (both
 * required), while this screen shows a resource *and* its related resources as a
 * single timeline — a requirement plus every data source in its history, an
 * insurance coverage plus its plan enrollments. So the set is fanned out into one
 * request per resource and merged by `selectorMergedAuditLogs`, newest-first.
 * (This replaces the removed `searchAuditLogs`, which took the resource set and
 * merged it server-side — WIN-15430.)
 *
 * Two consequences of fanning out, both deliberate:
 *
 * - **The request count follows the resource set.** It is one call per resource
 *   where the search route took one call total, and the set is not fixed —
 *   `useRequirementHistoryResources` grows with a requirement's
 *   `dataSourceHistory`. The set is not capped: dropping resources to save a
 *   request would silently understate a history the reader is entitled to see.
 * - **A single failing resource fails the whole timeline** (`Promise.all`, not
 *   `allSettled`). Rendering the surviving resources would present a partial
 *   audit trail as if it were complete, which is worse than surfacing the error.
 *
 * Keyed under the registry's `listAuditLogs` base key, so every mutation that
 * already declares that key in its `dependencies` still refreshes this timeline.
 */
export const useQueryAuditLogsForResources = (
  request: Request,
  config?: WSQueryConfig<AuditLog[], WSServiceError>
) =>
  useWSQuery<AuditLog[], WSServiceError>(
    [...queriesAuditLogs.listAuditLogs.queryKeyBase, "resourceSet", request],
    async () => {
      const lists = await Promise.all(
        request.resources.map(resource =>
          auditLogsApi.listAuditLogs({
            filter: {
              resourceType: { eq: resource.resourceType },
              resourceId: { eq: resource.resourceId }
            },
            page: { size: request.pageSize },
            xWingspanAccount: request.xWingspanAccount
          })
        )
      );

      return selectorMergedAuditLogs({ lists, pageSize: request.pageSize });
    },
    config
  );
